import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import axios from '@/lib/http';
import { AlertCircle, DollarSign, Calendar, TrendingUp, FileText, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface SubrogationOpportunity {
  trigger_type: string;
  category: string;
  probability_percent: number;
  projected_recovery: number;
  recovery_range_low: number;
  recovery_range_high: number;
  evidence_snippet: string;
  statute_expires?: string;
  days_until_expiration?: number;
  urgency_level: string;
  priority_score: number;
}

interface SubrogationAnalysis {
  opportunities: SubrogationOpportunity[];
  count: number;
  total_projected_recovery: number;
  highest_priority?: SubrogationOpportunity;
}

interface SubrogationPanelProps {
  reportId: string;
}

export default function SubrogationPanel({ reportId }: SubrogationPanelProps) {
  const { getToken } = useAuth();
  
  const [analysis, setAnalysis] = useState<SubrogationAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [letterContent, setLetterContent] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Run analysis when component mounts or expands
  const runAnalysis = useCallback(async () => {
    if (!reportId) {
      console.warn('No report ID provided');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await getToken();

      console.log('🔍 Running subrogation analysis for report:', reportId);

      const response = await axios.post(
        `${BACKEND_URL}/api/v1/subrogation/analyze/${reportId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('✅ Subrogation analysis result:', response.data);
      setAnalysis(response.data);
      
      // Auto-expand if opportunities found
      if (response.data.count > 0) {
        setExpanded(true);
      }
    } catch (error: any) {
      console.error('❌ Subrogation analysis error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to analyze subrogation';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [reportId, getToken]);

  useEffect(() => {
    runAnalysis();
  }, [runAnalysis]);

  const generateDemandLetter = async (opportunityIndex: number) => {
    try {
      setGeneratingLetter(true);
      const token = await getToken();

      const response = await axios.post(
        `${BACKEND_URL}/api/v1/subrogation/generate-letter/${reportId}`,
        {
          opportunity_index: opportunityIndex,
          include_evidence: true,
          include_case_law: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLetterContent(response.data.letter_content);
    } catch (error: any) {
      console.error('Error generating demand letter:', error);
      setErrorMessage('Failed to generate demand letter: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingLetter(false);
    }
  };

  // Urgency = fill-strength ladder in ONE hue (K3 law §2), on 1px borders — the urgency
  // word chip + icon carries state, not a 2px colored spine. critical: orange tint fill+border;
  // high: orange border only; medium: neutral hairline; low: ghost sub-border at dim ink.
  const getUrgencyColor = (urgency: string): React.CSSProperties => {
    switch (urgency) {
      case 'critical':
        return { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-text-primary)' }; // design-law-allow: K3-prescribed act-now tint tier
      case 'high':
        return { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-brand)', color: 'var(--color-text-primary)' };
      case 'medium':
        return { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' };
      case 'low':
        return { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border-sub)', color: 'var(--color-text-dim)' };
      default:
        return { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' };
    }
  };

  if (loading && !analysis) {
    return (
      <div className="border border-[var(--color-border)] rounded-md p-6 mb-6 bg-[var(--color-bg-surface)]">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: 'var(--color-brand)' }}></div>
          <p className="font-semibold text-[var(--color-text-primary)]">Scanning for subrogation opportunities...</p>
        </div>
      </div>
    );
  }

  if (error && !analysis) {
    return (
      <div className="border border-[var(--color-border)] rounded-md p-6 mb-6 bg-[var(--color-bg-surface)]">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
          <div>
            <p className="text-sm font-semibold text-[var(--color-text-primary)]">Subrogation Analysis Error</p>
            <p className="text-sm mt-1 text-[var(--color-text-secondary)]">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!analysis || analysis.count === 0) {
    return null; // Don't show anything if no opportunities
  }

  return (
    <div className="mb-6">
      {errorMessage && (
        <p className="text-sm mb-2 text-[var(--color-needs-human-text)]">{errorMessage}</p>
      )}
      {statusMessage && (
        <p className="text-sm mb-2" style={{ color: 'var(--color-verified)' }}>{statusMessage}</p>
      )}
      {/* Collapsed View - Alert Banner */}
      {!expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full border border-[var(--color-border)] rounded-md p-4 transition-all bg-[var(--color-bg-surface)]"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
              <div className="text-left">
                <h3 className="cs-h3 text-[var(--color-text-primary)]">
                  {analysis.count} Subrogation Opportunit{analysis.count === 1 ? 'y' : 'ies'} Detected
                </h3>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Total projected recovery: <span className="font-bold font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}><FinancialValue value={analysis.total_projected_recovery} /></span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
                HIGH PRIORITY
              </span>
              <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
            </div>
          </div>
        </button>
      )}

      {/* Expanded View - Full Analysis */}
      {expanded && (
        <div className="border border-[var(--color-border)] rounded-md p-6 bg-[var(--color-bg-surface)]">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <DollarSign className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
              <h3 className="cs-h2 text-[var(--color-text-primary)]">Subrogation Analysis</h3>
              <span className="px-2 py-1 rounded text-xs font-bold" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                {analysis.count} OPPORTUNITIES
              </span>
            </div>
            <button
              onClick={() => setExpanded(false)}
              className="flex items-center gap-1 text-sm font-semibold text-[var(--color-text-secondary)]"
            >
              <ChevronUp className="w-4 h-4" />
              Collapse
            </button>
          </div>

          {/* Summary Card */}
          <div className="rounded-md p-4 border-2 mb-6" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm mb-1 text-[var(--color-text-secondary)]">Total Projected Recovery:</p>
                <p className="text-3xl font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-verified)' }}>
                  <FinancialValue value={analysis.total_projected_recovery} />
                </p>
              </div>
              {analysis.highest_priority && (
                <div>
                  <p className="text-sm mb-1 text-[var(--color-text-secondary)]">Top Opportunity:</p>
                  <p className="cs-h3 text-[var(--color-text-primary)]">{analysis.highest_priority.trigger_type}</p>
                  <p className="text-sm font-mono tabular-nums text-[var(--color-text-secondary)]">
                    <FinancialValue value={analysis.highest_priority.projected_recovery} />
                    ({analysis.highest_priority.probability_percent}% probability)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Opportunities List */}
          <div className="space-y-4">
            {analysis.opportunities.map((opp, index) => (
              <div
                key={index}
                className="rounded-md p-4 border"
                style={getUrgencyColor(opp.urgency_level)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="cs-h3">{opp.trigger_type}</h4>
                      <span className="text-xs bg-[var(--color-bg-hover)] px-2 py-0.5 rounded font-semibold text-[var(--color-text-secondary)]">
                        {opp.category}
                      </span>
                    </div>
                    <p className="text-sm text-[var(--color-text-secondary)]">{opp.evidence_snippet}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-4 gap-4 mb-3">
                  <div>
                    <p className="text-xs font-semibold mb-1 text-[var(--color-text-secondary)]">Recovery Amount</p>
                    <p className="text-xl font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-verified)' }}>
                      <FinancialValue value={opp.projected_recovery} />
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)] font-mono tabular-nums">
                      Range: <FinancialValue value={opp.recovery_range_low} /> - <FinancialValue value={opp.recovery_range_high} />
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold mb-1 text-[var(--color-text-secondary)]">Success Probability</p>
                    <p className="text-xl font-bold font-mono tabular-nums text-right text-[var(--color-text-primary)]">{opp.probability_percent}%</p>
                    <div className="w-full bg-[var(--color-bg-elevated)] rounded-full h-2 mt-1">
                      <div
                        className="h-2 rounded-full"
                        style={{ width: `${opp.probability_percent}%`, backgroundColor: 'var(--color-verified)' }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold mb-1 text-[var(--color-text-secondary)]">Priority Score</p>
                    <p className="text-xl font-bold font-mono tabular-nums text-right text-[var(--color-text-primary)]">{opp.priority_score.toFixed(1)}</p>
                    <p className="text-xs text-[var(--color-text-muted)]">of 100</p>
                  </div>

                  {opp.days_until_expiration !== undefined && (
                    <div>
                      <p className="text-xs font-semibold mb-1 flex items-center gap-1 text-[var(--color-text-secondary)]">
                        <Calendar className="w-3 h-3" />
                        Statute Expires
                      </p>
                      <p className="text-xl font-bold font-mono tabular-nums text-right text-[var(--color-text-primary)]">
                        {opp.days_until_expiration} days
                      </p>
                      <p className="text-xs text-[var(--color-text-muted)] uppercase font-bold">
                        {opp.urgency_level} urgency
                      </p>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => generateDemandLetter(index)}
                  disabled={generatingLetter}
                  className="w-full px-4 py-2 rounded-md font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
                  style={{ backgroundColor: 'transparent', color: 'var(--color-brand)' }}
                >
                  <FileText className="w-4 h-4" />
                  {generatingLetter ? 'Generating...' : 'Generate Demand Letter'}
                </button>
              </div>
            ))}
          </div>

          {/* Demand Letter Modal */}
          {letterContent && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="rounded-md p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="cs-h2 text-[var(--color-text-primary)]">Demand Letter</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(letterContent);
                        setStatusMessage('Letter copied to clipboard!');
                      }}
                      className="px-4 py-2 rounded-lg"
                      style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => setLetterContent(null)}
                      className="px-4 py-2 rounded-lg bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]"
                    >
                      Close
                    </button>
                  </div>
                </div>
                <pre className="whitespace-pre-wrap font-mono text-sm p-4 rounded-md border bg-[var(--color-bg-base)] border-[var(--color-border)] text-[var(--color-text-primary)]">
                  {letterContent}
                </pre>
              </div>
            </div>
          )}

          {/* Info Footer */}
          <div className="mt-6 border border-[var(--color-border)] rounded-md p-3 flex items-start gap-2 bg-[var(--color-bg-elevated)]">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
            <div className="text-sm text-[var(--color-text-secondary)]">
              <strong className="text-[var(--color-text-primary)]">Court-Defensible Analysis:</strong> Opportunities identified using 42 verified trigger patterns
              with state-specific recovery rates, comparative fault analysis, and statute of limitations tracking.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
